import { IconType } from "react-icons";
import { PiDesktop, PiMoon, PiSun } from "react-icons/pi";
import { Modal } from "./Modal";
import { Settings, Theme } from "./settings";
import { LanguageDropdown } from "./LanguageDropdown";
import { lstr } from "./localization";

// ThemeChoice is a segmented control: each option names exactly the theme
// it applies, and the current one is visible at a glance.
function ThemeChoice({
  theme,
  setTheme,
  locale,
}: {
  theme: Theme;
  setTheme: (value: Theme) => void;
  locale: string;
}) {
  const strings = lstr(locale);
  const options: { value: Theme; label: string; icon: IconType }[] = [
    { value: Theme.Light, label: strings.settings_theme_light, icon: PiSun },
    { value: Theme.Dark, label: strings.settings_theme_dark, icon: PiMoon },
    {
      value: Theme.System,
      label: strings.settings_theme_system,
      icon: PiDesktop,
    },
  ];
  return (
    <div
      role="radiogroup"
      aria-label={strings.settings_theme}
      className="grid grid-cols-3 gap-1 rounded-2xl bg-cream-dark p-1"
    >
      {options.map(({ value, label, icon: Icon }) => {
        const selected = theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => setTheme(value)}
            className={`flex flex-col items-center gap-1 rounded-xl px-2 py-2.5 text-xs font-semibold transition-colors ${
              selected
                ? "bg-surface text-main-text shadow-card"
                : "text-secondary-text hover:text-main-text"
            }`}
          >
            <Icon size={20} className={selected ? "text-primary" : ""} />
            {label}
          </button>
        );
      })}
    </div>
  );
}

function SettingsMenu({
  settings,
  setSettings,
}: {
  settings: Settings;
  setSettings: (value: Settings) => void;
}) {
  const strings = lstr(settings.lLocale);
  const rowClass = "flex items-center justify-between gap-3";
  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <h3 className="eyebrow">{strings.settings_languages}</h3>
        <label className={rowClass}>
          <span className="text-[15px] text-main-text">
            {strings.language_i_speak}
          </span>
          <LanguageDropdown
            language={settings.lLocale}
            excludeLanguage={undefined}
            setLanguage={(value: string) =>
              setSettings({ ...settings, lLocale: value })
            }
          />
        </label>
        <label className={rowClass}>
          <span className="text-[15px] text-main-text">
            {strings.language_i_learn}
          </span>
          <LanguageDropdown
            language={settings.rLocale}
            excludeLanguage={settings.lLocale}
            setLanguage={(value: string) =>
              setSettings({ ...settings, rLocale: value })
            }
          />
        </label>
      </section>
      <section className="flex flex-col gap-3">
        <h3 className="eyebrow">{strings.settings_appearance}</h3>
        <ThemeChoice
          theme={settings.theme}
          setTheme={(value: Theme) =>
            setSettings({ ...settings, theme: value })
          }
          locale={settings.lLocale}
        />
      </section>
    </div>
  );
}

export function SettingsModal({
  settings,
  setSettings,
  closeModal,
}: {
  settings: Settings;
  setSettings: (value: Settings) => void;
  closeModal: () => void;
}) {
  return (
    <Modal
      showCloseButton={true}
      locale={settings.lLocale}
      closeModal={closeModal}
    >
      <h2 className="font-literata text-2xl font-bold text-main-text">
        {lstr(settings.lLocale).settings_title}
      </h2>
      <div className="mt-5">
        <SettingsMenu settings={settings} setSettings={setSettings} />
      </div>
    </Modal>
  );
}
